import { Link, TableCell } from "@material-ui/core";
import AttachmentIcon from '@material-ui/icons/Attachment';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MessageIcon from '@material-ui/icons/MessageOutlined';
import DoneIcon from '@material-ui/icons/Done';
import { Linked, pureRenderProps, useLinked, useModelCopy } from '@type-r/react';
import { asCurrency, asDate, asTime, CollapsableSection, Column, Columns, FormInput, FormLabel, FormLink, FormValue, InlineTable, TableRow, TopSection } from "lqx-react";
import * as React from "react";
import { Fragment, useEffect } from "react";
import { globalStore, localUserInfo } from "api";
import { InvoiceModel } from "api/invoice";
import { Attachments, DiffsTable, ModelDiff, CollectionDiff, getModelsJsonDeltaDiff, getChangedFieldsNames } from 'common';
import { DialogType } from './dialogs';
import { DisputeReason } from 'api/dispute';
import { ChangeTransaction } from "api/common";

const compareJsonProperties = {
    includeAttr: [
        'parts'
    ]
};

const PaymentDetails = ({ invoice }: { invoice: InvoiceModel }) => {

    let reconciliationInfos = invoice.reconciliationInvoiceInfos;

    return (
        <>
            {!reconciliationInfos.length ?
                <CollapsableSection id="payment-details" title="Payment Details">
                    <Columns>
                        <Column>
                            <FormInput readOnly label="Promise to Pay Date" date placeholder=" " value={invoice.promiseToPayDateUtc} />
                            <FormInput readOnly label="Payment Reference #" />
                            <FormInput readOnly label="Payment Amount" />
                            <FormInput readOnly label="Credit Note" />
                        </Column>
                        <Column>
                            <FormInput readOnly label="Discount" />
                            <FormInput readOnly label="Payment Execution Date" date placeholder=" " />
                            <FormInput readOnly label="Instrument" />
                            <FormInput readOnly label="Payment Completion Date" date placeholder=" " />
                        </Column>
                    </Columns>
                </CollapsableSection>
                :
                reconciliationInfos.map((reconciliationInfo, index) => (
                    <CollapsableSection id={`payment-details-${index}`} title={`Payment Details ${reconciliationInfos.length > 1 ? `#${index}` : ''}`}>
                        <Columns>
                            <Column>
                                <FormInput readOnly label="Promise to Pay Date" date placeholder="" value={invoice.promiseToPayDateUtc} />
                                <FormLink label="Payment Reference #" linkLabel={reconciliationInfo.paymentId} href={`/inmatch/bank-transactions/details/${reconciliationInfo.paymentId}`} />
                                <FormInput readOnly label="Payment Amount" value={asCurrency(reconciliationInfo.paymentAmount)} />
                                <FormInput readOnly label="Credit Note" />
                            </Column>
                            <Column>
                                <FormInput readOnly label="Discount" value={asCurrency(reconciliationInfo.discountAmount)} />
                                <FormInput readOnly label="Payment Execution Date" date placeholder=" " value={reconciliationInfo.paymentValueDate} />
                                <FormInput readOnly label="Instrument" />
                                <FormInput readOnly label="Payment Completion Date" date placeholder=" " value={reconciliationInfo.reconciliationBookedDate} />
                            </Column>
                        </Columns>
                    </CollapsableSection>
                ))}
        </>
    );
};

const FinancingDetails = () => {
    // TODO: Fill this with real data

    return (
        <CollapsableSection id="financing" title="Financing">
            <></>
        </CollapsableSection>
    );
};

export const CollapsibleTableRow = ({ children, details, tableRowClass = null }) => {
    const $isOpen = useLinked(false);
    return (
        <>
            <TableRow>
                {children}
                <TableCell
                    className="fit-width"
                    onClick={() => $isOpen.set(x => !x)}
                > { details ? <>
                    {$isOpen.value ?
                        <KeyboardArrowUpIcon />
                        :
                        <KeyboardArrowDownIcon />
                    }</> : undefined}
                </TableCell>
            </TableRow>
            {$isOpen.value ?
                <TableRow className={tableRowClass}>
                    <TableCell />
                    <TableCell colSpan={children.length}>
                        {details}
                    </TableCell>
                </TableRow>
                : null}
        </>
    );
};

const DisputeActivityView = ({ fetchedInvoice, disputeTransaction, disputeHistoryId }: { fetchedInvoice: InvoiceModel, disputeTransaction: ChangeTransaction, disputeHistoryId: number }) => {
    const dispute = fetchedInvoice.disputes.find(x => !!x.history.get(disputeHistoryId as any));
    if (!dispute) {
        return (<></>);
    }

    const activity = dispute.history.get(disputeHistoryId as any);

    useEffect(() => {
        if (Object.keys(activity.change).length === 0 && activity.change.constructor === Object) {
            const tmp = fetchedInvoice.clone();
            tmp.applyDisputeChanges(dispute, activity.versionNumber);
            const cumulativeDiff = getModelsJsonDeltaDiff(fetchedInvoice, tmp, compareJsonProperties);
            disputeTransaction.oldValues = cumulativeDiff.oldValues;
            disputeTransaction.deltaChange = cumulativeDiff.newValues;
        }
    }, []);

    let disputeReason = DisputeReason[dispute.reason];
    if (dispute.reason === 'Changed' || !isEmptyTransaction(disputeTransaction)) {

        disputeReason = getChangedFieldsNames(
            disputeTransaction.oldValues,
            disputeTransaction.deltaChange)
            .map(diff => InvoiceModel.diffNames[diff] || diff).join(', ');
    }
    return (
        <div>
            <span>Reason: {disputeReason}</span>
            {!isEmptyTransaction(disputeTransaction) ?
                <DiffView
                    fetchedInvoice={fetchedInvoice}
                    disputeTransaction={disputeTransaction}
                />
                : null}
        </div>);
};

const DiffView = ({ fetchedInvoice, disputeTransaction }: { fetchedInvoice: InvoiceModel, disputeTransaction: ChangeTransaction }) => {
    const prevInvoice = useModelCopy(fetchedInvoice),
        invoice = useModelCopy(prevInvoice);

    useEffect(() => {
        prevInvoice.set(disputeTransaction.oldValues, { parse: true, remove: false, merge: true });
        const allChanges = { ...disputeTransaction.oldValues, ...disputeTransaction.deltaChange };
        invoice.set(allChanges, { parse: true, remove: false, merge: true });
    }, [(invoice as any)._changeToken]);

    return (
        <DiffsTable>
            <ModelDiff label="HHH" fetched={prevInvoice} current={invoice} />
            <CollectionDiff titleAttr="partNumber" fetched={prevInvoice.parts} current={invoice.parts} />
        </DiffsTable>
    );
};

function isEmptyTransaction(changeTransaction: ChangeTransaction): boolean {
    if (!changeTransaction ||
        Object.keys(changeTransaction.deltaChange).length === 0) {
        return true;
    }
    return false;
}

const EventLogs = pureRenderProps({ fetchedInvoice: InvoiceModel }, ({ fetchedInvoice }) => {
    fetchedInvoice.logs.models.sort((a, b) => b.timeStamp.getTime() - a.timeStamp.getTime());
    const grouped = fetchedInvoice.logs.groupBy(x => asDate(x.timeStamp));

    return (
        <CollapsableSection id="all-activity" title="All Activity">
            <InlineTable noBorders>
                {Object.keys(grouped).map(date =>
                    <Fragment key={date}>
                        <TableRow className="group">
                            <TableCell colSpan={4}>
                                <FormLabel>{date}</FormLabel>
                            </TableCell>
                        </TableRow>

                        {grouped[date].map(event => {
                            const attachment = fetchedInvoice.uploads.get(event.attachmentId as any);
                            return (
                                <CollapsibleTableRow
                                    key={event.cid}
                                    details={
                                        <div>
                                            {attachment ?
                                                <div>
                                                    <span>Attachment: </span>
                                                    <Link onClick={() => attachment.download()}>{attachment.file_name}</Link>
                                                </div>
                                                : null}

                                            {event.comment ?
                                                <div>
                                                    <span>Comment: </span>
                                                    {event.comment}
                                                </div>
                                                : null}

                                            {event.documentType ?
                                                <div>
                                                    <span>Document Type: </span>
                                                    {event.documentType}
                                                </div>
                                                : null}

                                            {event.disputeHistoryId ?
                                                <DisputeActivityView
                                                    fetchedInvoice={fetchedInvoice}
                                                    disputeTransaction={event.disputeTransaction}
                                                    disputeHistoryId={event.disputeHistoryId}
                                                />
                                                : null}

                                            {!isEmptyTransaction(event.updateTransaction) ?
                                                <DiffView fetchedInvoice={fetchedInvoice}
                                                    disputeTransaction={event.updateTransaction} />
                                                : null}
                                        </div>
                                    }
                                >
                                    <TableCell className="fit-width">
                                        {
                                            event.event === 'File Uploaded' ? <AttachmentIcon /> :
                                                event.event === 'Commented FIX ME' ? <MessageIcon /> :
                                                    // TODO: add more icons or use the same event logs component as in orders
                                                    // when we know more about dispute management
                                                    <DoneIcon fontSize="small" />
                                        }

                                    </TableCell>
                                    <TableCell>
                                        {event.event}
                                    </TableCell>

                                    <TableCell className="fit-width">
                                        <FormValue>{event.author}</FormValue>
                                    </TableCell>
                                    <TableCell className="fit-width">
                                        <FormLabel>{asTime(event.timeStamp)}</FormLabel>
                                    </TableCell>
                                </CollapsibleTableRow>
                            );
                        })}
                    </Fragment>
                )}
            </InlineTable>
        </CollapsableSection>
    )
})

export interface AdditionalInfoProps {
    invoice: InvoiceModel;
    prevInvoice: InvoiceModel;
    $dialog: Linked<DialogType>;
}

export const AdditionalInfo = ({ invoice, prevInvoice, $dialog }: AdditionalInfoProps) => {
    let hasPaymentDetailsRights = localUserInfo.hasRole('Supplier') || localUserInfo.isInmatchManagerOrAnalyst;

    return (
        <TopSection id="additional-info" title="Additional Information">
            {
                localUserInfo.hasModulePermissions("Inmatch", invoice.conversationId) && hasPaymentDetailsRights
                    ? <PaymentDetails invoice={invoice} />
                    : <></>
            }
            <FinancingDetails />
            <Attachments
                $dialog={$dialog} uploads={invoice.uploads}
                disabledAdd={!invoice.hasWritePermissions} disabledRemove={!localUserInfo.isAdminWrite}
            />
            <EventLogs fetchedInvoice={prevInvoice} />
        </TopSection>
    );
};
